import { Document, Font, pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { ChartPage } from './chartPage';
import { LastPage } from './lastPage';
import font from 'assets/fonts/Quadraat-Light-ICONIQ.ttf';
import { InfoPage } from './infoPage';
import DownloadIcon from 'components/SectionCharts/DownloadIcon';
import BlackDownloadIcon from 'components/SectionCharts/BlackDownloadIcon';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { getCurrentDateFormatted } from 'utils/helpers';
import LoadingModal from 'components/LoadingModal';
import { downloadPDF } from 'network/downloadPDF';
import { toast } from 'react-toastify';
import Chart from 'chart.js/auto';
import { CHARTS_TITLES } from 'utils/constants';
import { setGeneratingPDF } from 'store/dashboardPDF/dashboardPDF.slice';
import { setLoading } from 'store/dashboardPDF/dashboardPDF.slice';
import { setDownloadIndex } from 'store/dashboardPDF/dashboardPDF.slice';
import { useAuth } from 'context/AuthContext';
import { PortCoInfoPage } from './portCoInfoPage';
import { TableKeys } from 'components/EditableTable/types';

export const DashboardPdf = () => {
  const { companyName, isPortCo } = useAuth();

  const pagesCount = isPortCo ? 20 : 18;

  const portCoData = useAppSelector(state => state.portCoData);
  const companyInfo = useAppSelector(state => state.companyInfo);
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const chartFilters = useAppSelector((state: RootState) => state.chartFilters);
  const chartSwitches = useAppSelector(
    (state: RootState) => state.chartSwitches
  );
  const disabledFilters = useAppSelector(
    (state: RootState) => state.disabledFilters.disabledFilters
  );
  const loading = useAppSelector(state => state.dashboardPDF.loading);
  const dispatch = useAppDispatch();

  const columns = portCoData.labels.slice(
    portCoData.defaultQuarterIdx - 7 < 0 ? 0 : portCoData.defaultQuarterIdx - 7,
    portCoData.defaultQuarterIdx + 1
  );

  const rows = portCoData.data.filter(row =>
    Object.values(TableKeys).includes(row.id as TableKeys)
  );

  const onDownload = () => {
    dispatch(setDownloadIndex(1));
    dispatch(setLoading(true));
    dispatch(setGeneratingPDF(true));
  };
  const onDownloadDone = () => {
    dispatch(setDownloadIndex(0));
    dispatch(setLoading(false));
    dispatch(setGeneratingPDF(false));
  };

  const exportChartToPNG = (chartElement: HTMLCanvasElement) => {
    const chart = Chart.getChart(chartElement);
    if (chart) {
      return chart.toBase64Image('image/png', 1);
    }
    return null;
  };

  const div2PDF = async () => {
    onDownload();
    document.body.classList.add('!overflow-hidden');
    const inputsClassNames: string[] = [];

    const data = {
      uuid:
        sessionStorage.getItem('uuid') || localStorage.getItem('uuid') || '',
      url: window.location.href,
      isDownload: true
    };

    try {
      await downloadPDF(data);
      toast.success('PDF download started', { hideProgressBar: true });
    } catch (error) {
      toast.error('Failed to download PDF', { hideProgressBar: true });
    }

    setTimeout(async () => {
      let inputs: HTMLCollectionOf<Element> =
        window.document.getElementsByClassName('div2PDF');

      for (let i = 0; i < inputs.length; i++) {
        inputsClassNames.push(inputs[i].classList.value);
        inputs[i].classList.value = 'min-w-[1200px] div2PDF';
      }

      // Give charts a moment to complete high-quality rendering
      await new Promise(resolve => setTimeout(resolve, 200));

      const canvasPromises = Array.from(inputs).map(async input =>
        exportChartToPNG(input as HTMLCanvasElement)
      );

      const canvasResults = await Promise.all(canvasPromises);

      Font.register({ family: 'Iconiq Quadraat', fonts: [{ src: font }] });

      const MyDocument = (
        <Document style={{ fontFamily: 'Iconiq Quadraat' }}>
          {isPortCo ? (
            <PortCoInfoPage
              data={rows}
              columns={columns}
              pagesCount={pagesCount}
              companyName={companyName}
              companyInfo={companyInfo}
            />
          ) : (
            <InfoPage
              pagesCount={pagesCount}
              companyName={companyName}
              companyInfo={companyInfo}
            />
          )}

          {canvasResults.map((img, i) => (
            <ChartPage
              key={i}
              image={img as string}
              pagesCount={pagesCount}
              title={CHARTS_TITLES[i]}
              chartFilters={chartFilters}
              chartSwitches={chartSwitches}
              disabledFilters={disabledFilters}
              pageNumber={i + (isPortCo ? 4 : 2)}
            />
          ))}

          <LastPage pagesCount={pagesCount} />
        </Document>
      );

      const pdfBlob = await pdf(MyDocument).toBlob();
      saveAs(
        pdfBlob,
        `ICONIQ Compass Benchmarks ${getCurrentDateFormatted()}.pdf`
      );

      for (let i = 0; i < inputs.length; i++) {
        inputs[i].classList.value = inputsClassNames[i];
      }

      onDownloadDone();
      document.body.classList.remove('!overflow-hidden');
    }, 500);
  };

  return (
    <div className='flex flex-row items-center gap-4'>
      <button
        className='bg-black dark:bg-white flex items-center gap-2 px-5 h-[40px] rounded-full hover:opacity-90'
        onClick={div2PDF}
        id='download_pdf_button'
        disabled={loading}
      >
        {isDarkMode ? <BlackDownloadIcon /> : <DownloadIcon />}
        <span className='text-white dark:text-black text-base font-light'>
          Download Your Benchmarks
        </span>
      </button>
      <LoadingModal isOpen={loading} onClose={() => {}} onReset={() => {}} />
    </div>
  );
};
